/**
 * comparePalette.js — Couleurs et styles de trait des sessions comparées
 * (page de comparaison, son PDF, graphique PM2.5 du PDF clinique vétérinaire).
 *
 * Pourquoi une palette dédiée : les lignes de seuil sont ambre (attention) et
 * rouge (alerte), les zones du graphique de session bleu clair, ambre et
 * rouge ; une courbe de session ne doit jamais être lue comme l'un d'eux.
 * Palette validée par Yannick (10.5.292), mesures vérifiées par
 * __tests__/comparePalette.test.js (ΔE2000 CIEDE2000, simulation Machado 2009
 * sévérité 1.0), en clair comme en sombre :
 *   - ΔE2000 ≥ 15 avec chaque couleur exclue (rouge, orange, ambre, vert de
 *     semanticColors et du thème, variantes sombres, zones de PMChart) ;
 *   - 4 premières couleurs (comparaison limitée à 4) : ΔE2000 ≥ 18 entre elles
 *     en deutéranopie et en protanopie ;
 *   - 6 couleurs : ΔE2000 ≥ 19 entre elles en vision normale ;
 *   - contraste ≥ 3:1 sur la carte du thème (blanc en clair, #282845 en sombre).
 * Ordre des rangs (D257, option A) : indigo, lime, cyan, pierre, fuchsia,
 * rose. Les deux premières sessions, cas le plus courant, ne partagent plus la
 * même famille de teinte (indigo et cyan étaient lus comme deux bleus).
 *
 * Style de trait (D257) : à l'écran, trait plein pour toutes les sessions, la
 * couleur suffit (4 au plus) et un motif y était lu comme un trou de mesure.
 * Dans les captures PDF (comparaison, PDF clinique vétérinaire), un motif par
 * rang prend le relais de la couleur en noir et blanc et au-delà de 6 sessions.
 *
 * Seuils : tirets [5, 4] épaisseur 1 (CompareChart). Sessions : épaisseur 2,
 * aucun motif ne reprend celui des seuils.
 */

// Rang i : même famille de teinte en clair et en sombre (indigo, lime, cyan,
// pierre, fuchsia, rose).
const LIGHT = Object.freeze(['#312E81', '#65A30D', '#0891B2', '#57534E', '#A21CAF', '#BE185D']);
const DARK  = Object.freeze(['#6366F1', '#D9F99D', '#A5F3FC', '#A8A29E', '#E879F9', '#FBCFE8']);

// Style de trait des sessions : 'solid' à l'écran, 'dashed' dans les captures PDF.
export const LINE_STYLES = Object.freeze({ SOLID: 'solid', DASHED: 'dashed' });

// Plein, tirets longs, tiret-point, tiret-point-point, tirets très longs, points.
export const SESSION_DASHES = Object.freeze([
  [], [14, 5], [10, 4, 2, 4], [10, 3, 2, 3, 2, 3], [22, 5], [2, 3],
]);

export const SESSION_LINE_WIDTH = 2;

const FILL_ALPHA = 0.12;

function toRgba(hex, alpha) {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Motif du rang selon le style de trait : toujours plein à l'écran. */
export const dashFor = (style, lineStyles) => (lineStyles === LINE_STYLES.DASHED ? style.dash : []);

/**
 * @param {boolean} darkMode
 * @returns {{ colors: string[], fills: string[], dashes: number[][],
 *   styleAt: (i: number) => { color: string, fill: string, dash: number[] } }}
 *   styleAt cycle au-delà de la longueur (PDF clinique à plus de 6 sessions).
 */
export function getComparePalette(darkMode = false) {
  const colors = darkMode ? DARK : LIGHT;
  const fills = colors.map(c => toRgba(c, FILL_ALPHA));
  const styleAt = (i) => {
    const k = ((i % colors.length) + colors.length) % colors.length;
    return { color: colors[k], fill: fills[k], dash: SESSION_DASHES[k % SESSION_DASHES.length] };
  };
  return { colors: [...colors], fills, dashes: SESSION_DASHES, styleAt };
}
